import type {
  EntityFieldItemApplier,
  EntityFieldItemOperation,
  EntityFieldValuesDeltaApplier,
  EntityFieldItemsUpdater,
  DocumentAdapter,
  DocumentAdapterContext,
  DocumentAdapterResult,
  PendingConnectionOperation,
  RemoveFromConnectionOperation,
} from '@github-ui/optimistic-connection'
import {createRecordDocumentAdapter, isEntityOperation} from '@github-ui/optimistic-connection'
import type {SettleTarget} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import type {ConnectionMutationTarget, SetFieldsMapper} from '@github-ui/optimistic-connection/pendingOperations'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {StateCountPageInfo} from '@github-ui/shared-dashboard-lists/shared/types/list-types'

import type {IssuesDashboardEnrichedDataPayload} from '../types/routes-types'

export type DashboardIssueDeletionConnection = ConnectionMutationTarget & {
  targets: SettleTarget[]
  createRemoveOperation?: (nodeId: string, issue?: DashboardIssueCoordinates) => DashboardIssueRemoveOperation
}
export type DashboardIssueDeleteOptions = {alreadySettled?: boolean}

const ID_PATH = ['id']
const LEGACY_GLOBAL_ID_PATH = ['globalId']

type EnrichedRow = IssuesDashboardEnrichedDataPayload['issues'][number]
type IssueRow = Extract<EnrichedRow, {itemType: 'issue'}>
type OptimisticRow = {
  itemType: 'issue' | 'pull_request'
  id: number | string
  globalId?: string
  state?: string | null
}
type IssueLabel = IssueRow['labels'][number]
type ProjectedLabel = Omit<IssueLabel, 'id'> & Required<Pick<IssueLabel, 'color' | 'nameHtml'>>
type ProjectedAssignee = Pick<IssueRow['assignees'][number], 'login' | 'avatarUrl'>
type RemovedDashboardIssue = Pick<IssueRow, 'state'>
type DashboardRowsResult<T> = {document: T[]; removedIssue?: RemovedDashboardIssue}
type DashboardCountName = 'totalCount' | 'openCount' | 'closedCount'
export type DashboardIssueCoordinates = {owner: string; repository: string; number: number}
export type DashboardIssueRemoveOperation = RemoveFromConnectionOperation & {dashboardNodeIds?: string[]}

function operationNodeIds(operation: RemoveFromConnectionOperation): Set<string> {
  const dashboardOperation = operation as DashboardIssueRemoveOperation
  return new Set(dashboardOperation.dashboardNodeIds ?? [operation.nodeId])
}

function matchesIssueCoordinates(row: IssueRow, issue: DashboardIssueCoordinates | undefined): boolean {
  return (
    issue !== undefined && row.number === issue.number && row.repoNameWithOwner === `${issue.owner}/${issue.repository}`
  )
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isProjectedLabel(value: unknown): value is ProjectedLabel {
  return (
    isRecord(value) &&
    typeof value.name === 'string' &&
    typeof value.nameHtml === 'string' &&
    typeof value.color === 'string' &&
    (value.description === undefined || typeof value.description === 'string')
  )
}

function mapLabels(labels: unknown[]): ProjectedLabel[] | undefined {
  if (!labels.every(isProjectedLabel)) return undefined

  return labels.map(label => ({
    name: label.name,
    nameHtml: label.nameHtml,
    color: label.color,
    ...(label.description === undefined ? {} : {description: label.description}),
  }))
}

function isProjectedAssignee(value: unknown): value is ProjectedAssignee {
  return isRecord(value) && typeof value.login === 'string' && typeof value.avatarUrl === 'string'
}

function mapAssignedActors(assignedActors: unknown): ProjectedAssignee[] | undefined {
  if (!isRecord(assignedActors) || !Array.isArray(assignedActors.nodes)) return undefined
  if (!assignedActors.nodes.every(isProjectedAssignee)) return undefined

  return assignedActors.nodes.map(assignee => ({login: assignee.login, avatarUrl: assignee.avatarUrl}))
}

function mapIssueFieldValue(
  value: Readonly<Record<string, unknown>>,
): NonNullable<IssueRow['issueFieldValues']>[number] | undefined {
  const field = value.field
  if (!isRecord(field) || typeof field.name !== 'string') return undefined
  const fieldName = field.name

  switch (value.__typename) {
    case 'IssueFieldTextValue':
      return typeof value.value === 'string' || value.value === null
        ? {fieldName, dataType: 'text', value: value.value}
        : undefined
    case 'IssueFieldSingleSelectValue':
      return typeof value.name === 'string' || value.name === null
        ? {fieldName, dataType: 'single_select', value: value.name}
        : undefined
    case 'IssueFieldMultiSelectValue': {
      if (!Array.isArray(value.options)) return undefined
      const options = value.options.map(option => (isRecord(option) ? option.name : undefined))
      return options.every((name): name is string => typeof name === 'string')
        ? {fieldName, dataType: 'multi_select', options}
        : undefined
    }
    case 'IssueFieldDateValue':
      return typeof value.value === 'string' || value.value === null
        ? {fieldName, dataType: 'date', value: value.value}
        : undefined
    case 'IssueFieldNumberValue':
      return typeof value.numberValue === 'number' || value.numberValue === null
        ? {fieldName, dataType: 'number', numberValue: value.numberValue}
        : undefined
    default:
      return undefined
  }
}

function fieldNameFromOperation(operation: EntityFieldItemOperation): string | undefined {
  const field = operation.item.field
  return isRecord(field) && typeof field.name === 'string' ? field.name : undefined
}

export const applyIssueEntityFieldItemToIssueRow: EntityFieldItemApplier = (entity, operation) => {
  if (operation.field !== 'issueFieldValues') return
  const fieldName = fieldNameFromOperation(operation)
  if (!fieldName) return

  if (operation.type === 'removeEntityFieldItem') {
    if (!Array.isArray(entity.issueFieldValues)) return
    const values = entity.issueFieldValues as NonNullable<IssueRow['issueFieldValues']>
    const index = values.findIndex(value => value.fieldName === fieldName)
    if (index !== -1) values.splice(index, 1)
    return
  }

  const item = mapIssueFieldValue(operation.item)
  if (!item) return
  const values = Array.isArray(entity.issueFieldValues)
    ? (entity.issueFieldValues as NonNullable<IssueRow['issueFieldValues']>)
    : (entity.issueFieldValues = [])
  const index = values.findIndex(value => value.fieldName === fieldName)
  if (index === -1) values.push(item)
  else values[index] = item
}

export const mapIssueViewerFieldsToIssueRow: SetFieldsMapper = operationFields => {
  const fields: Record<string, unknown> = {}
  if (typeof operationFields.title === 'string') fields.title = operationFields.title
  if (typeof operationFields.titleHTML === 'string') fields.titleHtml = operationFields.titleHTML
  if (typeof operationFields.state === 'string') {
    fields.state = operationFields.state
    // The issue's status doesn't carry the row's icon and color, so show the state icon until the server row arrives.
    fields.status = undefined
  }
  if (operationFields.stateReason === null || typeof operationFields.stateReason === 'string') {
    fields.stateReason = operationFields.stateReason
  }
  if (typeof operationFields.closed === 'boolean') fields.closed = operationFields.closed
  if (operationFields.closedAt === null || typeof operationFields.closedAt === 'string') {
    fields.closedAt = operationFields.closedAt
  }
  if (typeof operationFields.comments === 'number') fields.commentCount = operationFields.comments
  if (typeof operationFields.blockedBy === 'number') fields.blockedByCount = operationFields.blockedBy
  const issueDependenciesSummary = operationFields.issueDependenciesSummary
  if (isRecord(issueDependenciesSummary) && typeof issueDependenciesSummary.blockedBy === 'number') {
    fields.blockedByCount = issueDependenciesSummary.blockedBy
  }
  const subIssuesSummary = operationFields.subIssuesSummary
  if (
    isRecord(subIssuesSummary) &&
    typeof subIssuesSummary.total === 'number' &&
    typeof subIssuesSummary.completed === 'number'
  ) {
    fields.subIssuesSummary = {
      total: subIssuesSummary.total,
      completed: subIssuesSummary.completed,
      percentCompleted:
        subIssuesSummary.total === 0 ? 0 : Math.floor((subIssuesSummary.completed / subIssuesSummary.total) * 100),
    }
  }
  const issueType = operationFields.issueType
  if (issueType === null) {
    fields.issueType = null
  } else if (
    issueType &&
    typeof issueType === 'object' &&
    !Array.isArray(issueType) &&
    typeof (issueType as Record<string, unknown>).name === 'string' &&
    typeof (issueType as Record<string, unknown>).color === 'string'
  ) {
    fields.issueType = {
      name: (issueType as Record<string, unknown>).name,
      color: (issueType as Record<string, unknown>).color,
    }
  }
  const milestone = operationFields.milestone
  if (milestone === null) {
    fields.milestone = null
  } else if (isRecord(milestone) && typeof milestone.title === 'string' && typeof milestone.url === 'string') {
    fields.milestone = {
      title: milestone.title,
      url: milestone.url,
    }
  }
  if (Array.isArray(operationFields.labels)) {
    const labels = mapLabels(operationFields.labels)
    if (labels) fields.labels = labels
  }
  const assignees = mapAssignedActors(operationFields.assignedActors)
  if (assignees) fields.assignees = assignees
  return Object.keys(fields).length > 0 ? fields : undefined
}

export function mapIssueEntityFieldToIssueRow(field: string): string | undefined {
  if (field === 'comments') return 'commentCount'
  if (field === 'blockedBy') return 'blockedByCount'
  return undefined
}

export const applyIssueEntityFieldValuesDeltaToIssueRow: EntityFieldValuesDeltaApplier = (entity, operation) => {
  if (operation.field === 'issueDependenciesSummary') {
    const blockedByDelta = operation.deltas.blockedBy
    if (
      typeof entity.blockedByCount !== 'number' ||
      typeof blockedByDelta !== 'number' ||
      !Number.isFinite(blockedByDelta)
    )
      return false
    entity.blockedByCount = Math.max(0, entity.blockedByCount + blockedByDelta)
    return true
  }
  if (operation.field !== 'subIssuesSummary' || !isRecord(entity.subIssuesSummary)) return false
  const summary = entity.subIssuesSummary as Record<string, unknown>
  if (typeof summary.total !== 'number' || typeof summary.completed !== 'number') return false
  const totalDelta = operation.deltas.total ?? 0
  const completedDelta = operation.deltas.completed ?? 0
  if (!Number.isFinite(totalDelta) || !Number.isFinite(completedDelta)) return false

  const total = Math.max(0, summary.total + totalDelta)
  const completed = Math.min(total, Math.max(0, summary.completed + completedDelta))
  summary.total = total
  summary.completed = completed
  summary.percentCompleted = total === 0 ? 0 : Math.floor((completed / total) * 100)
  return true
}

export const updateIssueEntityFieldItemsInIssueRow: EntityFieldItemsUpdater = (entity, operation) => {
  if (operation.field !== 'linkedPullRequests') return false
  if (typeof entity.linkedPullRequestCount !== 'number' || !Array.isArray(entity.linkedPullRequests)) return false

  const removed = new Set(
    operation.removeKeys.map(linkedPullRequestKey).filter((key): key is string => key !== undefined),
  )
  const retained = (entity.linkedPullRequests as Array<Record<string, unknown>>).filter(item => {
    const key = typeof item.permalink === 'string' ? linkedPullRequestKey(item.permalink) : undefined
    return key === undefined || !removed.has(key)
  })
  for (const {key, item} of operation.upserts.toReversed()) {
    const summary = toIssueLinkedPullRequestSummary(item)
    if (!summary) continue
    const normalizedKey = linkedPullRequestKey(key)
    const existing = retained.findIndex(
      candidate =>
        typeof candidate.permalink === 'string' && linkedPullRequestKey(candidate.permalink) === normalizedKey,
    )
    if (existing !== -1) retained.splice(existing, 1)
    retained.unshift(summary)
  }

  entity.linkedPullRequests = retained.slice(0, 10)
  entity.linkedPullRequestCount = Math.max(0, entity.linkedPullRequestCount + operation.countDelta)
  return true
}

function linkedPullRequestKey(value: string): string | undefined {
  try {
    return new URL(value, 'https://github.com').pathname.replace(/\/$/, '')
  } catch {
    return undefined
  }
}

function toIssueLinkedPullRequestSummary(item: Record<string, unknown>): Record<string, unknown> | undefined {
  const repository = isRecord(item.repository) ? item.repository : undefined
  const permalink = typeof item.url === 'string' ? linkedPullRequestKey(item.url) : undefined
  if (!permalink || !repository || typeof repository.nameWithOwner !== 'string') return undefined
  return {
    title: typeof item.title === 'string' ? item.title : null,
    number: typeof item.number === 'number' ? item.number : null,
    permalink,
    repoNameWithOwner: repository.nameWithOwner,
    state: typeof item.state === 'string' ? item.state : null,
    isDraft: typeof item.isDraft === 'boolean' ? item.isDraft : null,
  }
}

export const DASHBOARD_ISSUE_DELETION_CONNECTION = {
  ownerId: 'issues-dashboard',
  connectionPath: ['results'],
}

const issueRecordOptions = {
  mapFields: mapIssueViewerFieldsToIssueRow,
  mapEntityField: mapIssueEntityFieldToIssueRow,
  applyEntityFieldItem: applyIssueEntityFieldItemToIssueRow,
  applyEntityFieldValuesDelta: applyIssueEntityFieldValuesDeltaToIssueRow,
  updateEntityFieldItems: updateIssueEntityFieldItemsInIssueRow,
}
const issueIdAdapter = createRecordDocumentAdapter({...issueRecordOptions, identityPath: ID_PATH})
const issueGlobalIdAdapter = createRecordDocumentAdapter({...issueRecordOptions, identityPath: LEGACY_GLOBAL_ID_PATH})

function isIssueRow(row: unknown): row is OptimisticRow & {itemType: 'issue'} {
  return (
    isRecord(row) &&
    row.itemType === 'issue' &&
    (typeof row.id === 'string' || typeof row.id === 'number') &&
    (row.globalId === undefined || typeof row.globalId === 'string')
  )
}

function applyIssueRow<T>(
  row: T,
  change: PendingConnectionOperation,
  context: DocumentAdapterContext,
): DocumentAdapterResult<T> {
  if (!isEntityOperation(change.operation) || !isIssueRow(row)) return undefined
  const adapter = typeof row.id === 'string' ? issueIdAdapter : issueGlobalIdAdapter
  return adapter(row, change, context)
}

export const dashboardRowDocumentAdapter: DocumentAdapter = applyIssueRow

function applyDashboardRows<T>(
  rows: T[],
  change: PendingConnectionOperation,
  context: DocumentAdapterContext,
): DashboardRowsResult<T> | undefined {
  if (isEntityOperation(change.operation)) {
    let affected = false
    const next = rows.map(row => {
      const result = applyIssueRow(row, change, context)
      if (!result) return row
      affected = true
      return result.document
    })
    if (!affected) return undefined
    return {document: next.every((row, index) => row === rows[index]) ? rows : next}
  }
  const {operation, ownerId} = change
  const connection = DASHBOARD_ISSUE_DELETION_CONNECTION
  if (
    operation.type !== 'removeFromConnection' ||
    ownerId !== connection.ownerId ||
    operation.connectionPath.length !== connection.connectionPath.length ||
    !operation.connectionPath.every((segment, index) => segment === connection.connectionPath[index])
  )
    return undefined

  const nodeIds = operationNodeIds(operation)
  const removedRow = rows.find(row => isIssueRow(row) && nodeIds.has(getListItemIdentity(row)))
  const next = rows.filter(row => !isIssueRow(row) || !nodeIds.has(getListItemIdentity(row)))
  return {
    document: next.length === rows.length ? rows : next,
    removedIssue:
      removedRow && isIssueRow(removedRow) && (removedRow.state === 'OPEN' || removedRow.state === 'CLOSED')
        ? {state: removedRow.state}
        : undefined,
  }
}

// Open and Closed counts can be absent, e.g. when the Active filter is selected.
function isDashboardPageInfo(value: unknown): value is StateCountPageInfo {
  return (
    isRecord(value) &&
    typeof value.currentPage === 'number' &&
    typeof value.totalPages === 'number' &&
    typeof value.totalCount === 'number'
  )
}

function applyDashboardCountRemoval(
  pageInfo: StateCountPageInfo,
  removedIssue: RemovedDashboardIssue | undefined,
  operation: RemoveFromConnectionOperation,
): StateCountPageInfo {
  if (removedIssue && !operation.countExpectations?.length) {
    const stateCountName: DashboardCountName = removedIssue.state === 'OPEN' ? 'openCount' : 'closedCount'
    return {
      ...pageInfo,
      totalCount: Math.max(0, pageInfo.totalCount - 1),
      ...(Number.isFinite(pageInfo[stateCountName])
        ? {[stateCountName]: Math.max(0, pageInfo[stateCountName] - 1)}
        : {}),
    }
  }

  let next = pageInfo
  const applicableCountNames = removedIssue
    ? new Set<DashboardCountName>(['totalCount', removedIssue.state === 'OPEN' ? 'openCount' : 'closedCount'])
    : undefined
  for (const expectation of operation.countExpectations ?? []) {
    const [countName, ...rest] = expectation.path
    if (
      rest.length > 0 ||
      (countName !== 'totalCount' && countName !== 'openCount' && countName !== 'closedCount') ||
      (applicableCountNames && !applicableCountNames.has(countName)) ||
      !Number.isFinite(expectation.before) ||
      !Number.isFinite(expectation.delta) ||
      !Number.isFinite(pageInfo[countName]) ||
      pageInfo[countName] < expectation.before
    ) {
      continue
    }
    if (next === pageInfo) next = {...pageInfo}
    next[countName] = Math.max(0, next[countName] + expectation.delta)
  }
  return next
}

export function createDashboardIssueRemoveOperation(
  rows: readonly unknown[] | undefined,
  pageInfo: unknown,
  nodeId: string,
  pendingOperations: readonly PendingConnectionOperation[] = [],
  issue?: DashboardIssueCoordinates,
): DashboardIssueRemoveOperation {
  const operation: DashboardIssueRemoveOperation = {
    type: 'removeFromConnection',
    connectionPath: DASHBOARD_ISSUE_DELETION_CONNECTION.connectionPath,
    nodeId,
  }
  if (!rows || !isDashboardPageInfo(pageInfo)) return operation
  const optimisticPage = pendingOperations.reduce<IssuesDashboardEnrichedDataPayload>(
    (page, change) => dashboardEnrichedDocumentAdapter(page, change, {})?.document ?? page,
    {issues: [...rows] as IssuesDashboardEnrichedDataPayload['issues'], pageInfo},
  )
  const row = optimisticPage.issues.find(candidate => {
    if (!isIssueRow(candidate)) return false
    return operationNodeIds(operation).has(getListItemIdentity(candidate)) || matchesIssueCoordinates(candidate, issue)
  })
  if (!isIssueRow(row)) return operation
  const resolvedOperation = {
    ...operation,
    dashboardNodeIds: [...new Set([nodeId, getListItemIdentity(row)])],
  }
  if (row.state !== 'OPEN' && row.state !== 'CLOSED') return resolvedOperation

  const stateCountName: DashboardCountName = row.state === 'OPEN' ? 'openCount' : 'closedCount'
  return {
    ...resolvedOperation,
    countExpectations: [
      {
        path: ['totalCount'],
        before: optimisticPage.pageInfo.totalCount,
        delta: -1,
      },
      ...(Number.isFinite(optimisticPage.pageInfo[stateCountName])
        ? [{path: [stateCountName], before: optimisticPage.pageInfo[stateCountName], delta: -1}]
        : []),
    ],
  }
}

export const dashboardEnrichedDocumentAdapter: DocumentAdapter<IssuesDashboardEnrichedDataPayload> = (
  document,
  change,
  context,
) => {
  if (!isRecord(document) || !Array.isArray(document.issues)) return undefined
  const result = applyDashboardRows(document.issues, change, context)
  if (!result) return undefined
  const pageInfo =
    change.operation.type === 'removeFromConnection'
      ? applyDashboardCountRemoval(document.pageInfo, result.removedIssue, change.operation)
      : document.pageInfo
  return {
    document:
      result.document === document.issues && pageInfo === document.pageInfo
        ? document
        : {...document, issues: result.document, pageInfo},
  }
}

export const dashboardMainDocumentAdapter: DocumentAdapter = (document, change, context) => {
  if (!isRecord(document) || !isRecord(document.payload) || !Array.isArray(document.payload.results)) return undefined
  const result = applyDashboardRows(document.payload.results, change, context)
  if (!result) return undefined
  const pageInfo = isDashboardPageInfo(document.payload.pageInfo)
    ? change.operation.type === 'removeFromConnection'
      ? applyDashboardCountRemoval(document.payload.pageInfo, result.removedIssue, change.operation)
      : document.payload.pageInfo
    : document.payload.pageInfo
  const pageInfoChanged = pageInfo !== document.payload.pageInfo
  return {
    document:
      result.document === document.payload.results && !pageInfoChanged
        ? document
        : {
            ...document,
            payload: {
              ...document.payload,
              results: result.document,
              ...(pageInfoChanged ? {pageInfo} : {}),
            },
          },
  }
}

export const dashboardDeferredDocumentAdapter: DocumentAdapter = (document, change, context) => {
  if (!isRecord(document) || !isRecord(document.payload)) return undefined
  const deferred = document.payload.issuesDashboardSurfaceContentDeferredData
  if (!isRecord(deferred) || !Array.isArray(deferred.results)) return undefined
  const result = applyDashboardRows(deferred.results, change, context)
  if (!result) return undefined
  return {
    document:
      result.document === deferred.results
        ? document
        : {
            ...document,
            payload: {
              ...document.payload,
              issuesDashboardSurfaceContentDeferredData: {...deferred, results: result.document},
            },
          },
  }
}

export function useOptimisticDashboardRows<T extends OptimisticRow>({
  base,
  targets,
}: {
  base: T[]
  /** The caches holding these rows: the list query, and the per-row node cache. */
  targets?: SettleTarget[]
}): T[] {
  const identityPath = base.some(row => row.itemType === 'issue' && typeof row.id === 'string')
    ? ID_PATH
    : LEGACY_GLOBAL_ID_PATH

  return useOptimisticData(base, {
    enabled: true,
    targets,
    identityPath,
    mapFields: mapIssueViewerFieldsToIssueRow,
    mapEntityField: mapIssueEntityFieldToIssueRow,
    applyEntityFieldItem: applyIssueEntityFieldItemToIssueRow,
    applyEntityFieldValuesDelta: applyIssueEntityFieldValuesDeltaToIssueRow,
    updateEntityFieldItems: updateIssueEntityFieldItemsInIssueRow,
  })
}

export function useOptimisticDashboardPage({
  base,
  targets,
}: {
  base: IssuesDashboardEnrichedDataPayload
  targets: SettleTarget[]
}): IssuesDashboardEnrichedDataPayload {
  return useOptimisticData(base, {
    enabled: true,
    targets,
    connections: [DASHBOARD_ISSUE_DELETION_CONNECTION],
    documentAdapter: dashboardEnrichedDocumentAdapter,
  })
}
